import axiosInstance from './axiosInstance';
import axiosV2Instance from './axiosV2Instance';
import { SiteMemberRole } from './sitesV2Api';

export interface SiteMember {
  id: number;
  siteId: number;
  userId: number;
  
  /** @deprecated Use roles property instead for multiple roles support */
  roleId?: number;
  
  userName?: string;
  
  /** @deprecated Use roles property instead for multiple roles support */
  roleName?: string;
  
  joinedAt: string;
  leftAt?: string;
  isActive: boolean; // Обязательное поле для совместимости с sitesV2Api
  // Плоская структура данных (как возвращает API)
  userClosed?: boolean;
  siteName?: string;
  siteClosed?: boolean;
  avatarUrl?: string; // URL аватарки пользователя (размер medium)
  addedBy?: number;
  removedBy?: number;
  addedByUserName?: string;
  removedByUserName?: string;
  
  // Новое: список ролей участника
  roles?: SiteMemberRole[];
}

// Простой кэш для участников (в памяти, на время сессии)
interface CacheItem {
  data: SiteMember[];
  timestamp: number;
}

const membersCache = new Map<string, CacheItem>();
const CACHE_DURATION = 60000; // 60 секунд - участники редко меняются

class SiteMembersApi {
  /**
   * Получить участников объекта (v2 API) с кешированием
   */
  async getSiteMembers(siteId: number, useCache: boolean = true): Promise<SiteMember[]> {
    const cacheKey = `site_members_${siteId}`;
    
    // Проверяем кеш
    if (useCache) {
      const cached = membersCache.get(cacheKey);
      const now = Date.now();
      
      if (cached && (now - cached.timestamp) < CACHE_DURATION) {
        // Возвращаем данные из кеша
        return cached.data;
      }
    }
    
    try {
      const response = await axiosV2Instance.get<SiteMember[]>(`/SitesV2/${siteId}/members`);
      const members = response.data;
      
      // Убеждаемся, что у всех участников есть поле isActive
      // Если API не возвращает isActive, вычисляем его на основе leftAt
      const membersWithIsActive = members.map(member => ({
        ...member,
        isActive: member.isActive !== undefined ? member.isActive : !member.leftAt
      }));
      
      // Сохраняем в кеш
      membersCache.set(cacheKey, {
        data: membersWithIsActive,
        timestamp: Date.now()
      });
      
      return membersWithIsActive;
    } catch (error) {
      console.error('Error fetching site members:', error);
      throw error;
    }
  }

  /**
   * Получить всех участников объекта (включая исключённых) — для истории
   */
  async getSiteMembersIncludingInactive(siteId: number): Promise<SiteMember[]> {
    try {
      const response = await axiosV2Instance.get<SiteMember[]>(
        `/sites/${siteId}/members`,
        { params: { includeInactive: true } }
      );
      const members = response.data;
      return members.map(member => ({
        ...member,
        isActive: member.isActive !== undefined ? member.isActive : !member.leftAt
      }));
    } catch (error) {
      console.error('Error fetching site members including inactive:', error);
      throw error;
    }
  }

  /**
   * Получить активных участников объекта (не покинувших объект) с кешированием
   */
  async getActiveSiteMembers(siteId: number, useCache: boolean = true): Promise<SiteMember[]> {
    const cacheKey = `active_site_members_${siteId}`;
    
    // Проверяем кеш для активных участников
    if (useCache) {
      const cached = membersCache.get(cacheKey);
      const now = Date.now();
      
      if (cached && (now - cached.timestamp) < CACHE_DURATION) {
        // Возвращаем данные из кеша
        return cached.data;
      }
    }
    
    try {
      // Получаем всех участников (используем кеш)
      const members = await this.getSiteMembers(siteId, useCache);
      // Фильтруем активных участников (не покинувших объект и с isActive = true)
      const activeMembers = members.filter(member => !member.leftAt && member.isActive !== false);
      
      // Сохраняем активных участников в кеш отдельно
      membersCache.set(cacheKey, {
        data: activeMembers,
        timestamp: Date.now()
      });
      
      return activeMembers;
    } catch (error) {
      console.error('Error fetching active site members:', error);
      throw error;
    }
  }

  /**
   * Инвалидировать кеш для участников объекта (использовать при добавлении/удалении участников)
   */
  invalidateCache(siteId: number): void {
    membersCache.delete(`site_members_${siteId}`);
    membersCache.delete(`active_site_members_${siteId}`);
  }

  /**
   * Очистить весь кеш участников
   */
  clearCache(): void {
    membersCache.clear();
  }

  /**
   * Покинуть объект (самопокидание текущего пользователя)
   */
  async leaveSite(siteId: number): Promise<void> {
    try {
      await axiosV2Instance.post(`/sites/${siteId}/members/leave`);
      // Инвалидируем кеш после покидания объекта
      this.invalidateCache(siteId);
    } catch (error) {
      console.error('Error leaving site:', error);
      throw error;
    }
  }

  /**
   * Получить полную историю ролей участника объекта (назначения и отзывы)
   */
  async getSiteMemberRoleHistory(siteId: number, siteMemberId: number): Promise<SiteMemberRole[]> {
    try {
      const response = await axiosV2Instance.get<SiteMemberRole[]>(
        `/sites/${siteId}/members/${siteMemberId}/roles/history`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching site member role history:', error);
      throw error;
    }
  }

  /**
   * Получить всех пользователей системы (для добавления в проект)
   */
  async getAllUsers(): Promise<SiteMember[]> {
    try {
      const response = await axiosInstance.get<SiteMember[]>('/users');
      return response.data;
    } catch (error) {
      console.error('Error fetching all users:', error);
      throw error;
    }
  }
}

export const siteMembersApi = new SiteMembersApi();
